import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const QuickActions = () => {
  const [activeAction, setActiveAction] = useState(null);
  const [alertPrice, setAlertPrice] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');

  const quickActionItems = [
    {
      id: 'buy',
      title: 'Quick Buy',
      description: 'Execute instant buy order',
      icon: 'ShoppingCart',
      color: 'text-success bg-success/10 hover:bg-success/20',
      action: () => setActiveAction('buy')
    },
    {
      id: 'sell',
      title: 'Quick Sell',
      description: 'Execute instant sell order',
      icon: 'TrendingDown',
      color: 'text-error bg-error/10 hover:bg-error/20',
      action: () => setActiveAction('sell')
    },
    {
      id: 'alert',
      title: 'Price Alert',
      description: 'Set price notification',
      icon: 'Bell',
      color: 'text-warning bg-warning/10 hover:bg-warning/20',
      action: () => setActiveAction('alert')
    },
    {
      id: 'portfolio',
      title: 'Add to Portfolio',
      description: 'Track new cryptocurrency',
      icon: 'Plus',
      color: 'text-primary bg-primary/10 hover:bg-primary/20',
      action: () => window.location.href = '/portfolio-tracker'
    }
  ];

  const cryptoOptions = [
    { symbol: 'BTC', name: 'Bitcoin', price: 45250.00 },
    { symbol: 'ETH', name: 'Ethereum', price: 2845.50 },
    { symbol: 'ADA', name: 'Cardano', price: 0.485 },
    { symbol: 'SOL', name: 'Solana', price: 98.45 }
  ];

  const recentActions = [
    {
      id: 1,
      type: 'buy',
      crypto: 'BTC',
      amount: 0.1,
      price: 44800.00,
      timestamp: new Date(Date.now() - 1800000),
      status: 'completed'
    },
    {
      id: 2,
      type: 'alert',
      crypto: 'ETH',
      targetPrice: 3000.00,
      timestamp: new Date(Date.now() - 3600000),
      status: 'active'
    },
    {
      id: 3,
      type: 'sell',
      crypto: 'ADA',
      amount: 500,
      price: 0.492,
      timestamp: new Date(Date.now() - 7200000),
      status: 'completed'
    }
  ];

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ago`;
    return `${minutes}m ago`;
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'buy': return 'ShoppingCart';
      case 'sell': return 'TrendingDown';
      case 'alert': return 'Bell';
      default: return 'Activity';
    }
  };

  const getActionColor = (type) => {
    switch (type) {
      case 'buy': return 'text-success';
      case 'sell': return 'text-error';
      case 'alert': return 'text-warning';
      default: return 'text-primary';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'active': return 'text-primary bg-primary/10';
      case 'pending': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const handleCreateAlert = () => {
    if (alertPrice && selectedCrypto) {
      // Mock alert creation
      console.log(`Alert created for ${selectedCrypto} at $${alertPrice}`);
      setActiveAction(null);
      setAlertPrice('');
    }
  };

  const ActionModal = () => {
    if (!activeAction) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-card-foreground">
              {activeAction === 'buy' ? 'Quick Buy Order' : 
               activeAction === 'sell' ? 'Quick Sell Order' : 'Create Price Alert'}
            </h3>
            <button
              onClick={() => setActiveAction(null)}
              className="p-2 hover:bg-muted rounded-md transition-standard"
            >
              <Icon name="X" size={16} className="text-muted-foreground" />
            </button>
          </div>

          {activeAction === 'alert' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Cryptocurrency
                </label>
                <select
                  value={selectedCrypto}
                  onChange={(e) => setSelectedCrypto(e?.target?.value)}
                  className="w-full p-3 border border-border rounded-lg bg-background text-foreground"
                >
                  {cryptoOptions?.map((crypto) => (
                    <option key={crypto?.symbol} value={crypto?.symbol}>
                      {crypto?.name} ({crypto?.symbol}) - ${crypto?.price?.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Alert Price"
                type="number"
                placeholder="Enter target price"
                value={alertPrice}
                onChange={(e) => setAlertPrice(e?.target?.value)}
              />
              <div className="flex space-x-3">
                <Button variant="outline" onClick={() => setActiveAction(null)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleCreateAlert} className="flex-1">
                  Create Alert
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  Cryptocurrency
                </label>
                <select
                  value={selectedCrypto}
                  onChange={(e) => setSelectedCrypto(e?.target?.value)}
                  className="w-full p-3 border border-border rounded-lg bg-background text-foreground"
                >
                  {cryptoOptions?.map((crypto) => (
                    <option key={crypto?.symbol} value={crypto?.symbol}>
                      {crypto?.name} ({crypto?.symbol}) - ${crypto?.price?.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Amount (USD)"
                type="number"
                placeholder="Enter amount"
              />
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  This is a demo interface. In a real application, this would connect to your exchange account.
                </p>
              </div>
              <div className="flex space-x-3">
                <Button variant="outline" onClick={() => setActiveAction(null)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  variant={activeAction === 'buy' ? 'default' : 'destructive'} 
                  onClick={() => setActiveAction(null)} 
                  className="flex-1"
                >
                  {activeAction === 'buy' ? 'Place Buy Order' : 'Place Sell Order'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-card border border-border rounded-lg shadow-elevation-1">
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Zap" size={20} className="text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">Quick Actions</h2>
              <p className="text-sm text-muted-foreground">Fast trading and portfolio management</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Quick Action Buttons */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {quickActionItems?.map((item) => (
              <button
                key={item?.id}
                onClick={item?.action}
                className={`p-4 rounded-lg border border-border transition-standard hover:scale-105 ${item?.color}`}
              >
                <div className="flex items-center space-x-3">
                  <Icon name={item?.icon} size={20} />
                  <div className="text-left">
                    <p className="font-medium">{item?.title}</p>
                    <p className="text-xs opacity-80">{item?.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Recent Actions */}
          <div>
            <h3 className="text-lg font-semibold text-card-foreground mb-4">Recent Actions</h3>
            <div className="space-y-3">
              {recentActions?.map((action) => (
                <div key={action?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getActionColor(action?.type)} bg-current/10`}>
                      <Icon name={getActionIcon(action?.type)} size={16} className={getActionColor(action?.type)} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-card-foreground">
                        {action?.type === 'alert' ? 
                          `Price alert for ${action?.crypto} at $${action?.targetPrice?.toLocaleString()}` :
                          `${action?.type?.charAt(0)?.toUpperCase() + action?.type?.slice(1)} ${action?.amount} ${action?.crypto} at $${action?.price?.toLocaleString()}`
                        }
                      </p>
                      <p className="text-xs text-muted-foreground">{formatTime(action?.timestamp)}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(action?.status)}`}>
                    {action?.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-border bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-success" />
              <span className="text-sm text-muted-foreground">Secure trading environment</span>
            </div>
            <Button variant="ghost" size="sm">
              <Icon name="History" size={16} />
              View All
            </Button>
          </div>
        </div>
      </div>
      <ActionModal />
    </>
  );
};

export default QuickActions;